import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  Card,
  CardContent,
  Grid,
  Checkbox,
  TableSortLabel,
  TextField,
  InputAdornment,
  useTheme,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Block as IgnoreIcon,
  Replay as ReopenIcon,
  Search as SearchIcon,
  CheckCircleOutline as ClearIcon,
  AutoFixHigh as AutoFixIcon,
} from '@mui/icons-material';
import { fleetSyncAPI } from '../services/api';

const ConflictsPage = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  
  const [conflictGroups, setConflictGroups] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [sortBy, setSortBy] = useState('device_imei');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [resolving, setResolving] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVehicles, setSelectedVehicles] = useState({});
  const [selectedGroups, setSelectedGroups] = useState(new Set());
  const [server1, setServer1] = useState('');
  const [server2, setServer2] = useState('');
  const [availableServers, setAvailableServers] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [autoResolving, setAutoResolving] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showWrongClient, setShowWrongClient] = useState(false);
  const [wrongClientData, setWrongClientData] = useState([]);
  const [wrongClientLoading, setWrongClientLoading] = useState(false);
  const [selectedWrongClients, setSelectedWrongClients] = useState(new Set());

  useEffect(() => {
    fetchAvailableServers();
    fetchSummary();
  }, []);

  useEffect(() => {
    fetchConflictData();
  }, [filter, server1, server2, page, rowsPerPage, debouncedSearch]);

  // Debounce search input (300ms) and reset page to 0 on search change
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchAvailableServers = async () => {
    try {
      const response = await fleetSyncAPI.getServersWithConflicts();
      const data = response.data;
      setAvailableServers(Array.isArray(data) ? data : (data?.results || []));
    } catch (error) {
      console.error('Error fetching servers:', error);
      setAvailableServers([]);
    }
  };

  const fetchSummary = async () => {
    try {
      const summaryRes = await fleetSyncAPI.getConflictsSummary();
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const fetchWrongClientConflicts = async () => {
    try {
      setWrongClientLoading(true);
      const response = await fleetSyncAPI.getWrongClientConflicts();
      setWrongClientData(response.data || []);
    } catch (error) {
      console.error('Error fetching wrong client conflicts:', error);
      setWrongClientData([]);
    } finally {
      setWrongClientLoading(false);
    }
  };

  const handleReassignBatch = async () => {
    if (selectedWrongClients.size === 0) return;
    try {
      setResolving('batch');
      const items = (Array.isArray(wrongClientData) ? wrongClientData : [])
        .filter(row => selectedWrongClients.has(row.vehicle_id))
        .map(row => ({
          vehicle_id: row.vehicle_id,
          correct_client_id: row.correct_client_id,
        }));
      const response = await fleetSyncAPI.reassignBatch({ items });
      const data = response.data;
      setSnackbarMessage(
        data.error_count > 0
          ? `Reassigned ${data.success_count} vehicle(s), ${data.error_count} failed`
          : `Successfully reassigned ${data.success_count} vehicle(s)`
      );
      setSnackbarSeverity(data.error_count > 0 ? 'warning' : 'success');
      setSnackbarOpen(true);
      setSelectedWrongClients(new Set());
      await fetchWrongClientConflicts();
    } catch (error) {
      console.error('Error batch reassigning:', error);
      setSnackbarMessage(error.response?.data?.error || 'Failed to reassign vehicles');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setResolving(null);
    }
  };

  const fetchConflictData = async () => {
    try {
      setLoading(true);
      const params = {
        ...(filter !== 'all' ? { status: filter } : {}),
        ...(server1 ? { server1_id: server1 } : {}),
        ...(server2 ? { server2_id: server2 } : {}),
        ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
        page: page + 1,
        page_size: rowsPerPage,
      };
      const vehiclesRes = await fleetSyncAPI.getConflictVehicles(params);
      const data = vehiclesRes.data;
      if (data && data.results && Array.isArray(data.results)) {
        setConflictGroups(data.results);
        setTotalCount(data.count || 0);
      } else {
        setConflictGroups(data || []);
        setTotalCount((data || []).length);
      }
    } catch (error) {
      console.error('Error fetching conflict data:', error);
      setConflictGroups([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveVehicleConflict = async (vehicleId) => {
    try {
      setResolving(vehicleId);
      await fleetSyncAPI.resolveVehicleConflict({ keep_vehicle_id: vehicleId });
      await Promise.all([fetchConflictData(), fetchSummary()]);
    } catch (error) {
      console.error('Error resolving vehicle conflict:', error);
      alert('Failed to resolve vehicle conflict');
    } finally {
      setResolving(null);
    }
  };

  const handleIgnoreVehicleConflict = async (vehicleId) => {
    try {
      setResolving(vehicleId);
      await fleetSyncAPI.ignoreVehicleConflict(vehicleId);
      await Promise.all([fetchConflictData(), fetchSummary()]);
    } catch (error) {
      console.error('Error ignoring vehicle conflict:', error);
      alert('Failed to ignore vehicle conflict');
    } finally {
      setResolving(null);
    }
  };

  const handleReopenVehicleConflict = async (vehicleId) => {
    try {
      setResolving(vehicleId);
      await fleetSyncAPI.reopenVehicleConflict(vehicleId);
      await Promise.all([fetchConflictData(), fetchSummary()]);
    } catch (error) {
      console.error('Error reopening vehicle conflict:', error);
      alert('Failed to reopen vehicle conflict');
    } finally {
      setResolving(null);
    }
  };

  const handleClearVehicleConflict = async (vehicleId) => {
    try {
      setResolving(vehicleId);
      await fleetSyncAPI.clearVehicleConflict(vehicleId);
      await Promise.all([fetchConflictData(), fetchSummary()]);
    } catch (error) {
      console.error('Error clearing vehicle conflict:', error);
      alert('Failed to clear vehicle conflict');
    } finally {
      setResolving(null);
    }
  };

  const handleAutoResolveSingleServer = async () => {
    try {
      setAutoResolving(true);
      const response = await fleetSyncAPI.autoResolveSingleServerConflicts({});
      const data = response.data;
      setSnackbarMessage(
        data.resolved_count > 0
          ? `Auto-resolved ${data.resolved_count} conflict(s), ignored ${data.ignored_count} vehicle(s)`
          : 'No single-server conflicts found to auto-resolve'
      );
      setSnackbarSeverity(data.resolved_count > 0 ? 'success' : 'info');
      setSnackbarOpen(true);
      await Promise.all([fetchConflictData(), fetchSummary()]);
      setSelectedGroups(new Set());
      setSelectedVehicles({});
    } catch (error) {
      console.error('Error auto-resolving conflicts:', error);
      setSnackbarMessage(error.response?.data?.error || 'Failed to auto-resolve conflicts');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setAutoResolving(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      resolved: 'success',
      ignored: 'default'
    };
    return colors[status] || 'default';
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Sort groups (client-side, server already paginated)
  const sortedGroups = [...conflictGroups].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case 'device_imei':
        aValue = a.imei || '';
        bValue = b.imei || '';
        break;
      case 'count':
        aValue = a.count;
        bValue = b.count;
        break;
      default:
        aValue = a.imei || '';
        bValue = b.imei || '';
    }

    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, display: 'flex', justifyContent: 'center', width: '90%', mx: 'auto' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 4, width: '90%', mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Vehicle Conflicts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage vehicles that share the same GPS device
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {selectedGroups.size > 0 && (
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckIcon />}
              onClick={async () => {
                try {
                  setResolving('batch');
                  for (const imei of selectedGroups) {
                    const vehicleId = selectedVehicles[imei];
                    if (vehicleId) {
                      await fleetSyncAPI.resolveVehicleConflict({ keep_vehicle_id: vehicleId });
                    }
                  }
                  await Promise.all([fetchConflictData(), fetchSummary()]);
                  setSelectedGroups(new Set());
                  setSelectedVehicles({});
                } catch (error) {
                  console.error('Error batch resolving:', error);
                  alert('Failed to resolve some conflicts');
                } finally {
                  setResolving(null);
                }
              }}
              disabled={resolving === 'batch'}
            >
              Resolve Selected ({selectedGroups.size})
            </Button>
          )}
          <Button
            variant="outlined"
            color="info"
            startIcon={<AutoFixIcon />}
            onClick={handleAutoResolveSingleServer}
            disabled={autoResolving}
            sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          >
            Auto-Resolve Single-Server
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchConflictData()}
          >
            Actualiser
          </Button>
        </Box>
      </Box>

      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: isDarkMode ? alpha(theme.palette.error.main, 0.15) : alpha(theme.palette.error.main, 0.1),
              border: `1px solid ${isDarkMode ? alpha(theme.palette.error.main, 0.3) : alpha(theme.palette.error.main, 0.4)}`
            }}>
              <CardContent>
                <Typography color="error.dark" variant="body2" sx={{ fontWeight: 500 }}>
                  Total Conflict Vehicles
                </Typography>
                <Typography variant="h4" sx={{ mt: 1, color: 'error.dark', fontWeight: 600 }}>
                  {summary.conflict_vehicles || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: isDarkMode ? alpha(theme.palette.warning.main, 0.15) : alpha(theme.palette.warning.main, 0.1),
              border: `1px solid ${isDarkMode ? alpha(theme.palette.warning.main, 0.3) : alpha(theme.palette.warning.main, 0.4)}`
            }}>
              <CardContent>
                <Typography color="warning.dark" variant="body2" sx={{ fontWeight: 500 }}>
                  Pending
                </Typography>
                <Typography variant="h4" sx={{ mt: 1, color: 'warning.dark', fontWeight: 600 }}>
                  {summary.pending}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: isDarkMode ? alpha(theme.palette.success.main, 0.15) : alpha(theme.palette.success.main, 0.1),
              border: `1px solid ${isDarkMode ? alpha(theme.palette.success.main, 0.3) : alpha(theme.palette.success.main, 0.4)}`
            }}>
              <CardContent>
                <Typography color="success.dark" variant="body2" sx={{ fontWeight: 500 }}>
                  Resolved
                </Typography>
                <Typography variant="h4" sx={{ mt: 1, color: 'success.dark', fontWeight: 600 }}>
                  {summary.resolved}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ 
              bgcolor: isDarkMode ? alpha(theme.palette.grey[500], 0.15) : alpha(theme.palette.grey[500], 0.1),
              border: `1px solid ${isDarkMode ? alpha(theme.palette.grey[500], 0.3) : alpha(theme.palette.grey[500], 0.4)}`
            }}>
              <CardContent>
                <Typography color="text.primary" variant="body2" sx={{ fontWeight: 500 }}>
                  Ignored
                </Typography>
                <Typography variant="h4" sx={{ mt: 1, color: 'text.primary', fontWeight: 600 }}>
                  {summary.ignored}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper sx={{ 
        mb: 3,
        bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.8) : 'background.paper',
        backgroundImage: 'none'
      }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
            <TextField
              placeholder="Search by IMEI, vehicle name, or client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flexGrow: 1, minWidth: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={() => { setFilter('all'); setPage(0); }}
                variant={filter === 'all' ? 'contained' : 'outlined'}
                size="small"
              >
                All
              </Button>
              <Button
                onClick={() => { setFilter('pending'); setPage(0); }}
                variant={filter === 'pending' ? 'contained' : 'outlined'}
                color="warning"
                size="small"
              >
                Pending
              </Button>
              <Button
                onClick={() => { setFilter('resolved'); setPage(0); }}
                variant={filter === 'resolved' ? 'contained' : 'outlined'}
                color="success"
                size="small"
              >
                Resolved
              </Button>
              <Button
                onClick={() => { setFilter('ignored'); setPage(0); }}
                variant={filter === 'ignored' ? 'contained' : 'outlined'}
                color="inherit"
                size="small"
              >
                Ignored
              </Button>
              <Button
                onClick={() => {
                  setShowWrongClient(!showWrongClient);
                  if (!showWrongClient) {
                    fetchWrongClientConflicts();
                  }
                }}
                variant={showWrongClient ? 'contained' : 'outlined'}
                color="error"
                size="small"
              >
                Wrong Client
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Server 1</InputLabel>
                <Select
                  value={server1}
                  onChange={(e) => {
                    setServer1(e.target.value);
                    setPage(0);
                    if (e.target.value === server2) {
                      setServer2('');
                    }
                  }}
                  label="Server 1"
                >
                  <MenuItem value="">Aucun</MenuItem>
                  {(Array.isArray(availableServers) ? availableServers : []).map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Server 2</InputLabel>
                <Select
                  value={server2}
                  onChange={(e) => { setServer2(e.target.value); setPage(0); }}
                  label="Server 2"
                  disabled={!server1}
                >
                  <MenuItem value="">Aucun</MenuItem>
                  {(Array.isArray(availableServers) ? availableServers : []).filter(s => s.id !== parseInt(server1)).map(s => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => fetchConflictData()}
                size="small"
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </Box>

        {showWrongClient && selectedWrongClients.size > 0 && (
          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckIcon />}
              onClick={handleReassignBatch}
              disabled={resolving === 'batch'}
            >
              Reassign Selected ({selectedWrongClients.size})
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSelectedWrongClients(new Set())}
            >
              Clear Selection
            </Button>
          </Box>
        )}

        <TableContainer>
          {showWrongClient ? (
            wrongClientLoading ? (
              <Box sx={{ p: 8, textAlign: 'center' }}>
                <CircularProgress />
              </Box>
            ) : wrongClientData.length === 0 ? (
              <Box sx={{ p: 8, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No wrong client conflicts found
                </Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead sx={{ bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.1) : 'grey.50' }}>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selectedWrongClients.size > 0 && selectedWrongClients.size < wrongClientData.length}
                        checked={wrongClientData.length > 0 && selectedWrongClients.size === wrongClientData.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedWrongClients(new Set(wrongClientData.map(r => r.vehicle_id)));
                          } else {
                            setSelectedWrongClients(new Set());
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell><strong>IMEI</strong></TableCell>
                    <TableCell><strong>Vehicle</strong></TableCell>
                    <TableCell><strong>Server</strong></TableCell>
                    <TableCell><strong>Current Client</strong></TableCell>
                    <TableCell><strong>Correct Client</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {wrongClientData.map((row) => (
                    <TableRow key={`${row.imei}-${row.server_id}`} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedWrongClients.has(row.vehicle_id)}
                          onChange={(e) => {
                            const newSelected = new Set(selectedWrongClients);
                            if (e.target.checked) {
                              newSelected.add(row.vehicle_id);
                            } else {
                              newSelected.delete(row.vehicle_id);
                            }
                            setSelectedWrongClients(newSelected);
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600 }}>
                          {row.imei}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.vehicle_name}</TableCell>
                      <TableCell>
                        <Chip label={row.server_name} size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="error">
                          {row.current_client_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          fleet_user_id: {row.current_fleet_user_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: 'success.main' }}>
                          {row.correct_client_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          fleet_user_id: {row.correct_fleet_user_id}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          startIcon={<CheckIcon />}
                          disabled={resolving === row.vehicle_id || !row.correct_client_id}
                          onClick={async () => {
                            try {
                              setResolving(row.vehicle_id);
                              await fleetSyncAPI.reassignToCorrectClient({
                                vehicle_id: row.vehicle_id,
                                correct_client_id: row.correct_client_id,
                              });
                              setSnackbarMessage(`Vehicle reassigned to ${row.correct_client_name}`);
                              setSnackbarSeverity('success');
                              setSnackbarOpen(true);
                              await fetchWrongClientConflicts();
                            } catch (error) {
                              console.error('Error reassigning vehicle:', error);
                              setSnackbarMessage(error.response?.data?.error || 'Failed to reassign vehicle');
                              setSnackbarSeverity('error');
                              setSnackbarOpen(true);
                            } finally {
                              setResolving(null);
                            }
                          }}
                        >
                          Reassign
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : sortedGroups.length === 0 ? (
            <Box sx={{ p: 8, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No vehicle conflicts found
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead sx={{ bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.1) : 'grey.50' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedGroups.size > 0 && selectedGroups.size < sortedGroups.length}
                      checked={sortedGroups.length > 0 && selectedGroups.size === sortedGroups.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedGroups(new Set(sortedGroups.map(g => g.imei)));
                        } else {
                          setSelectedGroups(new Set());
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'device_imei'}
                      direction={sortBy === 'device_imei' ? sortOrder : 'asc'}
                      onClick={() => handleSort('device_imei')}
                    >
                      <strong>IMEI</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell><strong>Conflicting Vehicles</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedGroups.map((group) => (
                  <TableRow 
                    key={group.imei}
                    hover
                    selected={selectedGroups.has(group.imei)}
                    sx={{ 
                      '&:hover': { 
                        bgcolor: isDarkMode ? alpha(theme.palette.primary.main, 0.08) : 'action.hover' 
                      }
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedGroups.has(group.imei)}
                        onChange={(e) => {
                          const newSelected = new Set(selectedGroups);
                          if (e.target.checked) {
                            newSelected.add(group.imei);
                          } else {
                            newSelected.delete(group.imei);
                          }
                          setSelectedGroups(newSelected);
                        }}
                        disabled={!group.vehicles.some(v => v.conflict_status === 'conflict') || !selectedVehicles[group.imei]}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, px: 2, verticalAlign: 'top' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 600 }}>
                        {group.imei}
                      </Typography>
                      <Chip 
                        label={`${group.count} vehicles`} 
                        size="small" 
                        color="error"
                        sx={{ mt: 1, fontSize: '0.7rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2, px: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {group.vehicles.map((vehicle) => (
                          <Box 
                            key={vehicle.id} 
                            sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 2,
                              p: 1.5,
                              border: 1,
                              borderColor: 'divider',
                              borderRadius: 1,
                              bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.5) : 'background.paper'
                            }}
                          >
                            <Checkbox
                              size="small"
                              sx={{ p: 0 }}
                              checked={selectedVehicles[group.imei] === vehicle.id}
                              onChange={() => {
                                setSelectedVehicles(prev => ({
                                  ...prev,
                                  [group.imei]: vehicle.id
                                }));
                                // Auto-select the group when a vehicle is selected
                                if (group.vehicles.some(v => v.conflict_status === 'conflict')) {
                                  setSelectedGroups(prev => {
                                    const newSelected = new Set(prev);
                                    newSelected.add(group.imei);
                                    return newSelected;
                                  });
                                }
                              }}
                            />
                            <Box sx={{ flexGrow: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {vehicle.name}
                                </Typography>
                                {vehicle.conflict_status && vehicle.conflict_status !== 'conflict' && (
                                  <Chip
                                    label={vehicle.conflict_status}
                                    color={getStatusColor(vehicle.conflict_status)}
                                    size="small"
                                    sx={{ height: 18, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Box>
                              <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap' }}>
                                {vehicle.client_name && (
                                  <Typography variant="caption" color="text.secondary">
                                    Client: {vehicle.client_name}
                                  </Typography>
                                )}
                                {vehicle.server_name && (
                                  <Chip 
                                    label={vehicle.server_name} 
                                    size="small" 
                                    color="info"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                  />
                                )}
                                {vehicle.registration_number && (
                                  <Typography variant="caption" color="text.secondary">
                                    Reg: {vehicle.registration_number}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 2, px: 2, verticalAlign: 'top' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {group.vehicles.some(v => v.conflict_status === 'conflict') ? (
                          <>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<CheckIcon />}
                              disabled={resolving === group.imei || !selectedVehicles[group.imei]}
                              onClick={async () => {
                                if (!selectedVehicles[group.imei]) {
                                  alert('Please select a vehicle to keep');
                                  return;
                                }
                                try {
                                  setResolving(group.imei);
                                  await fleetSyncAPI.resolveVehicleConflict({ keep_vehicle_id: selectedVehicles[group.imei] });
                                  await fetchConflictData();
                                  setSelectedVehicles(prev => {
                                    const newState = { ...prev };
                                    delete newState[group.imei];
                                    return newState;
                                  });
                                } catch (error) {
                                  console.error('Error resolving conflict:', error);
                                  alert('Failed to resolve conflict');
                                } finally {
                                  setResolving(null);
                                }
                              }}
                            >
                              Resolve
                            </Button>
                            <Button
                              variant="outlined"
                              color="inherit"
                              size="small"
                              startIcon={<IgnoreIcon />}
                              disabled={resolving === group.imei}
                              onClick={async () => {
                                try {
                                  setResolving(group.imei);
                                  for (const vehicle of group.vehicles) {
                                    await fleetSyncAPI.ignoreVehicleConflict(vehicle.id);
                                  }
                                  await fetchConflictData();
                                  setSelectedVehicles(prev => {
                                    const newState = { ...prev };
                                    delete newState[group.imei];
                                    return newState;
                                  });
                                } catch (error) {
                                  console.error('Error ignoring conflicts:', error);
                                  alert('Failed to ignore conflicts');
                                } finally {
                                  setResolving(null);
                                }
                              }}
                            >
                              Ignore All
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            startIcon={<ReopenIcon />}
                            disabled={resolving === group.imei}
                            onClick={async () => {
                              try {
                                setResolving(group.imei);

                                for (const vehicle of group.vehicles) {
                                  try {
                                    await fleetSyncAPI.reopenVehicleConflict(vehicle.id);
                                  } catch (vehicleError) {
                                    throw vehicleError;
                                  }
                                }

                                await Promise.all([fetchConflictData(), fetchSummary()]);
                              } catch (error) {
                                alert(`Failed to reopen conflicts: ${error.response?.data?.error || error.message}`);
                              } finally {
                                setResolving(null);
                              }
                            }}
                          >
                            Reopen All
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      </Paper>

      {!showWrongClient && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Lignes par page:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
        />
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbarSeverity}
          onClose={() => setSnackbarOpen(false)}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ConflictsPage;
